import { useState, useEffect, useCallback, useRef } from 'react';
import { API_URL, ITEMS_PER_PAGE } from '../constants';

/**
 * Manages garment fetching, filter application, and infinite-scroll pagination.
 */
export function useGarments(session) {
  const [garments, setGarments] = useState([]);
  const [filteredGarments, setFilteredGarments] = useState([]);
  const [displayedGarments, setDisplayedGarments] = useState([]);
  const [selectedGarmentIndex, setSelectedGarmentIndex] = useState(0);
  const [filters, setFilters] = useState({
    gender: 'female',
    priceMin: 0,
    priceMax: Infinity,
    category: '',
    searchQuery: '',
  });
  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreItems, setHasMoreItems] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const sessionTokenRef = useRef(null);
  const prevFiltersRef = useRef(null);

  // Fetch garments when session token changes (new session)
  useEffect(() => {
    if (!session?.session_token) return;
    if (sessionTokenRef.current === session.session_token) return;
    sessionTokenRef.current = session.session_token;

    const fetchGarments = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public-studio/session/products`, {
          headers: { 'X-Session-Token': session.session_token },
        });
        if (!response.ok) throw new Error('Failed to load products');
        const data = await response.json();
        const products = data.products || [];
        setGarments(products);
        setFilteredGarments(products);
        const uniqueCategories = [...new Set(products.map((p) => p.category).filter(Boolean))];
        setCategories(uniqueCategories);
      } catch (err) {
        console.error('Failed to fetch garments:', err);
      }
    };

    fetchGarments();
  }, [session]);

  // Apply filters whenever garments list or filters change
  useEffect(() => {
    let filtered = [...garments];

    if (filters.gender) {
      filtered = filtered.filter(
        (g) =>
          (g.gender || '').toLowerCase() === filters.gender ||
          (g.gender || '').toLowerCase() === 'unisex'
      );
    }
    if (filters.category) {
      filtered = filtered.filter(
        (g) => (g.category || '').toLowerCase() === filters.category.toLowerCase()
      );
    }
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(
        (g) =>
          (g.product_name || g.name || '').toLowerCase().includes(query) ||
          (g.sku_id || '').toLowerCase().includes(query)
      );
    }
    if (filters.priceMax < Infinity || filters.priceMin > 0) {
      filtered = filtered.filter((g) => {
        const price = g.selling_price || g.price || 0;
        return price >= filters.priceMin && price <= filters.priceMax;
      });
    }

    setFilteredGarments(filtered);

    const filtersChanged = JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters);
    if (filtersChanged || !prevFiltersRef.current) {
      setSelectedGarmentIndex(0);
      setCurrentPage(0);
      prevFiltersRef.current = { ...filters };
    }

    setDisplayedGarments(filtered.slice(0, ITEMS_PER_PAGE));
    setHasMoreItems(filtered.length > ITEMS_PER_PAGE);
  }, [filters, garments]);

  // Load next page of garments for the carousel
  const loadMoreGarments = useCallback(() => {
    if (loadingMore || !hasMoreItems) return;
    setLoadingMore(true);
    const nextPage = currentPage + 1;
    const startIndex = nextPage * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const newItems = filteredGarments.slice(startIndex, endIndex);
    if (newItems.length > 0) {
      setDisplayedGarments((prev) => [...prev, ...newItems]);
      setCurrentPage(nextPage);
      setHasMoreItems(endIndex < filteredGarments.length);
    } else {
      setHasMoreItems(false);
    }
    setLoadingMore(false);
  }, [currentPage, filteredGarments, hasMoreItems, loadingMore]);

  return {
    garments,
    filteredGarments,
    displayedGarments,
    selectedGarmentIndex,
    setSelectedGarmentIndex,
    filters,
    setFilters,
    categories,
    hasMoreItems,
    loadingMore,
    loadMoreGarments,
  };
}
